import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip, LinearProgress,
  CircularProgress, Alert, TextField, Divider
} from '@mui/material';
import {
  ManageAccounts as SmIcon,
  CheckCircleOutline as DoneIcon,
  RadioButtonUnchecked as ActiveIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  FiberNew as NewIcon
} from '@mui/icons-material';
import { get } from '../../services/apiService';
import { format } from 'date-fns';

const PRIMARY = '#1A2E22';
const SM_COLOR = '#6a1b9a'; // purple — distinct from the 4 pillar colours
const MUTED = '#5f6368';
const SURFACE = '#F8F9FA';

// ── Mini donut (same as ReliabilityProductEngineering) ───────────────────────
function MiniDonut({ value = 0 }) {
  const v = Math.min(Math.max(Number(value), 0), 100);
  const color = v === 100 ? '#2e7d32' : v >= 67 ? '#4caf50' : v >= 33 ? '#ff9800' : '#f44336';
  const r = 14, stroke = 3.5, circ = 2 * Math.PI * r;
  const dash = (v / 100) * circ;
  return (
    <Box sx={{ position: 'relative', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width="36" height="36" style={{ position: 'absolute', transform: 'rotate(-90deg)' }}>
        <circle cx="18" cy="18" r={r} fill="none" stroke={`${color}22`} strokeWidth={stroke} />
        <circle cx="18" cy="18" r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
      </svg>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color, lineHeight: 1 }}>{Math.round(v)}%</Typography>
    </Box>
  );
}

// ── State helpers ─────────────────────────────────────────────────────────────
const DONE_STATES = ['Closed', 'Done', 'Completed'];
const ACTIVE_STATES = ['Active', 'In Progress'];

function stateColor(state) {
  if (DONE_STATES.includes(state)) return 'success';
  if (ACTIVE_STATES.includes(state)) return 'primary';
  if (state === 'Removed') return 'default';
  return 'warning';
}

function completionFromState(state, features) {
  if (!features) return 0;
  const done = features.filter(f => DONE_STATES.includes(f.state)).length;
  return features.length ? Math.round((done / features.length) * 100) : 0;
}

// ── Feature row ───────────────────────────────────────────────────────────────
const FeatureRow = ({ feature, isLast }) => {
  const comp = DONE_STATES.includes(feature.state) ? 100
    : ACTIVE_STATES.includes(feature.state) ? 50 : 0;

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        px: 2.5, py: 1.75,
        borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.06)',
        '&:hover': { bgcolor: `${SM_COLOR}06` },
        transition: 'background 0.15s'
      }}
    >
      <MiniDonut value={comp} />

      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={500} color="#1a1a1a" noWrap>
          {feature.title}
        </Typography>
        {feature.tags && (
          <Typography variant="caption" color={MUTED} noWrap display="block">
            {feature.tags}
          </Typography>
        )}
      </Box>

      <Chip
        label={feature.state || 'Unknown'}
        color={stateColor(feature.state)}
        size="small"
        variant="outlined"
        sx={{ minWidth: 100, justifyContent: 'center', fontSize: '0.72rem' }}
      />

      {feature.targetDate ? (
        <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 120, justifyContent: 'flex-end' }}>
          <ScheduleIcon sx={{ fontSize: 14, color: MUTED }} />
          <Typography variant="caption" color={MUTED}>
            {format(new Date(feature.targetDate), 'dd MMM yyyy')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ minWidth: 120 }} />
      )}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const ServiceManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await get('/api/reliability/service-management/data');
      if (res.success) setData(res.data);
      else setError('Failed to load Service Management data');
    } catch (e) {
      setError(e.message || 'Failed to load Service Management data');
    } finally {
      setLoading(false);
    }
  };

  const features = data?.features || [];

  const filteredFeatures = useMemo(() => features.filter(f => {
    if (stateFilter === 'done' && !DONE_STATES.includes(f.state)) return false;
    if (stateFilter === 'active' && !ACTIVE_STATES.includes(f.state)) return false;
    if (stateFilter === 'new' && (DONE_STATES.includes(f.state) || ACTIVE_STATES.includes(f.state))) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!f.title?.toLowerCase().includes(q) && !f.tags?.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [features, search, stateFilter]);

  const totalCount     = features.length;
  const doneCount      = features.filter(f => DONE_STATES.includes(f.state)).length;
  const activeCount    = features.filter(f => ACTIVE_STATES.includes(f.state)).length;
  const newCount       = features.filter(f => !DONE_STATES.includes(f.state) && !ACTIVE_STATES.includes(f.state)).length;
  const completionPct  = completionFromState(null, features);

  // ── Loading / error / empty ────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress sx={{ color: SM_COLOR }} />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  if (!data) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 6, textAlign: 'center', borderRadius: '20px', border: '1.5px solid rgba(0,0,0,0.07)' }}
      >
        <SmIcon sx={{ fontSize: 56, color: `${SM_COLOR}60`, mb: 2 }} />
        <Typography variant="h6" color={MUTED} fontWeight={600}>No Service Management data yet</Typography>
        <Typography variant="body2" color={MUTED} mt={1}>
          Configure the ADO epic URL in Admin → Reliability &amp; Scale, then trigger a pull.
        </Typography>
      </Paper>
    );
  }

  // ── Summary card ──────────────────────────────────────────────────────────
  return (
    <Box>
      <Paper
        elevation={0}
        sx={{ borderRadius: '20px', border: '1.5px solid rgba(0,0,0,0.07)', overflow: 'hidden', mb: 3 }}
      >
        {/* Header band */}
        <Box sx={{ px: 3, py: 2.5, bgcolor: `${SM_COLOR}0d`, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px', flexShrink: 0,
            bgcolor: `${SM_COLOR}1a`,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <SmIcon sx={{ color: SM_COLOR, fontSize: 26 }} />
          </Box>
          <Box flex={1} minWidth={0}>
            <Typography variant="h6" fontWeight={600} color="#1a1a1a" lineHeight={1.3}>
              {data.epicTitle || `Epic #${data.epicId}`}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
              <Chip label={data.epicState || 'Unknown'} color={stateColor(data.epicState)} size="small" variant="outlined" />
              <Typography variant="caption" color={MUTED}>Project: {data.adoProjectName}</Typography>
              {data.pulledAt && (
                <Typography variant="caption" color={MUTED}>
                  · Updated {format(new Date(data.pulledAt), 'dd MMM yyyy, HH:mm')}
                </Typography>
              )}
            </Box>
          </Box>
          <Box textAlign="right" flexShrink={0}>
            <Typography sx={{ fontSize: '2.5rem', fontWeight: 700, color: SM_COLOR, lineHeight: 1 }}>
              {completionPct}%
            </Typography>
            <Typography variant="caption" color={MUTED} fontWeight={500}>complete</Typography>
          </Box>
        </Box>

        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={completionPct}
          sx={{
            height: 6,
            bgcolor: `${SM_COLOR}18`,
            '& .MuiLinearProgress-bar': { bgcolor: SM_COLOR }
          }}
        />

        {/* Stat tiles */}
        <Box sx={{ display: 'flex', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          {[
            { label: 'Total Features', value: totalCount, color: PRIMARY },
            { label: 'Completed',      value: doneCount,  color: '#2e7d32' },
            { label: 'In Progress',    value: activeCount, color: '#1565c0' },
            { label: 'Not Started',    value: newCount,   color: '#e65100' }
          ].map(({ label, value, color }, i, arr) => (
            <Box
              key={label}
              flex={1}
              sx={{
                py: 2, textAlign: 'center',
                borderRight: i < arr.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none'
              }}
            >
              <Typography variant="h5" fontWeight={700} color={color} lineHeight={1}>{value}</Typography>
              <Typography variant="caption" color={MUTED} fontWeight={500} mt={0.5} display="block">{label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* ── Feature list ─────────────────────────────────────────────────── */}
      {/* Toolbar */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: '16px', border: '1.5px solid rgba(0,0,0,0.07)', bgcolor: SURFACE }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search features…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: MUTED, mr: 0.5, fontSize: 18 }} /> }}
            sx={{
              minWidth: 240,
              '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'white' }
            }}
          />
          <Box display="flex" gap={1}>
            {[
              { key: 'all',    label: 'All' },
              { key: 'active', label: 'In Progress' },
              { key: 'done',   label: 'Completed' },
              { key: 'new',    label: 'Not Started' }
            ].map(({ key, label }) => (
              <Chip
                key={key}
                label={label}
                size="small"
                onClick={() => setStateFilter(key)}
                variant={stateFilter === key ? 'filled' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  bgcolor: stateFilter === key ? SM_COLOR : 'white',
                  color: stateFilter === key ? 'white' : 'inherit',
                  borderColor: stateFilter === key ? SM_COLOR : 'rgba(0,0,0,0.2)',
                  fontWeight: stateFilter === key ? 600 : 400,
                  '&:hover': { bgcolor: stateFilter === key ? SM_COLOR : `${SM_COLOR}10` }
                }}
              />
            ))}
          </Box>
          <Chip
            label={`${filteredFeatures.length} feature${filteredFeatures.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ ml: 'auto', bgcolor: 'white', fontWeight: 600 }}
          />
        </Box>
      </Paper>

      {/* Feature rows */}
      {filteredFeatures.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: '16px', border: '1.5px solid rgba(0,0,0,0.07)' }}>
          <Typography variant="body2" color={MUTED}>
            {search || stateFilter !== 'all' ? 'No features match the current filter.' : 'No features found under this epic.'}
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1.5px solid rgba(0,0,0,0.07)', overflow: 'hidden' }}>
          {/* Column header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.25, bgcolor: SURFACE, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Box sx={{ width: 36 }} />
            <Typography variant="caption" fontWeight={600} color={MUTED} flex={1}>Feature</Typography>
            <Typography variant="caption" fontWeight={600} color={MUTED} sx={{ minWidth: 100, textAlign: 'center' }}>State</Typography>
            <Typography variant="caption" fontWeight={600} color={MUTED} sx={{ minWidth: 120, textAlign: 'right' }}>Target Date</Typography>
          </Box>
          {filteredFeatures.map((f, i) => (
            <FeatureRow key={f.id} feature={f} isLast={i === filteredFeatures.length - 1} />
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default ServiceManagement;
